import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Printer, CreditCard, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API, api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { InvoiceDocument } from "@/components/InvoiceDocument";
import { StatusBadge } from "@/components/common";

export default function PublicInvoice() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(!!sessionId);
  const load = () => fetch(`${API}/public/invoices/${token}`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setData).catch(() => setErr(true));
  useEffect(() => { load(); }, [token]); // eslint-disable-line
  useEffect(() => {
    if (!sessionId) return;
    let tries = 0, timer;
    const poll = async () => {
      try {
        const { data: s } = await api.get(`/payments/status/${sessionId}`);
        if (s.payment_status === "paid") { setChecking(false); toast.success(t("paymentReceived")); return load(); }
      } catch {}
      if (++tries < 8) timer = setTimeout(poll, 2000); else setChecking(false);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]); // eslint-disable-line
  const pay = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/public/invoices/${token}/pay`, { origin_url: window.location.origin });
      window.location.href = d.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const unpaid = data && data.invoice.status !== "paid";
  return (
    <div className="min-h-screen bg-[#F6F7F4] p-4 sm:p-10" data-testid="public-invoice-page">
      <div className="no-print mx-auto mb-6 flex max-w-[820px] flex-wrap items-center gap-3">
        <Link to="/"><img src="/images/logo-compact-dark.png" alt="Mohassib" className="h-12 w-auto rounded-lg bg-[#04140B] px-2 py-1" /></Link>
        {data && <StatusBadge status={data.invoice.status} />}
        {checking && <span data-testid="public-invoice-checking" className="inline-flex items-center gap-1 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />{t("payChecking")}</span>}
        {data?.invoice.paid_online && <span data-testid="public-invoice-paid-online" className="inline-flex items-center gap-1 text-sm text-[#007A3D]"><CheckCircle2 className="h-4 w-4" />{t("paidOnline")}</span>}
        <div className="ms-auto flex gap-2">
          {unpaid && !checking && <button data-testid="public-invoice-pay-btn" disabled={busy} onClick={pay} className="inline-flex items-center gap-2 rounded-full bg-[#007A3D] px-5 py-2 text-sm font-bold text-white hover:bg-[#005E2E] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}{t("payNow")} · {(data.invoice.currency && data.invoice.currency !== "AED" ? data.invoice.fx_total : data.invoice.total).toFixed(2)} {data.invoice.currency || "AED"}</button>}
          {data && <button data-testid="public-invoice-print-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full bg-[#0B1220] px-4 py-2 text-sm font-bold text-white hover:bg-slate-800"><Printer className="h-4 w-4" />{t("print")} / PDF</button>}
        </div>
      </div>
      {err && <p data-testid="public-invoice-notfound" className="text-center text-slate-500">{t("noData")}</p>}
      {data && <InvoiceDocument inv={data.invoice} co={data.company} />}
      <p className="no-print mx-auto mt-6 max-w-[820px] text-center text-xs text-slate-400">{t("securePayNote")}</p>
    </div>
  );
}
